import {
  BreadcrumbProps,
  Category,
  type ICategory,
  PLPBreadcrumbTransformer,
  categoryApi,
  categoryListTransformer,
} from '@commerce-product-webcomponents/sdk';
import { PLPHeaderCard } from '@commerce-product-webcomponents/ui';
import { ProductListingContainer } from '@commerce-product-webcomponents/ui-composite';
import { Product } from '@commerce-search-webcomponents/sdk';
import { ErrorCard, Errors } from '@commerce-webcomponents/ui';
import {
  sendUtagView,
  transformerRegistry,
  useNextgenRouter,
  useTealiumDataLayer,
  useTranslation,
} from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import { useEffect, useState } from 'react';
import { EventNameEnum } from '../../components/Tealium/types/propTypes';
import { useInternalNavigationTracker } from '../providers/InternalNavigationTrackerProvider/InternalNavigationTrackerProvider';
import styles from './product.module.scss';
import { PlpContainerProps } from './types/propTypes';
import Layout from 'app/components/Layout/Layout';
import { getHomeBreadcrumb } from 'app/utils/common';
import { getLocale } from 'i18n/routingUtil';
import logObject from 'utils/logObject';

const defaultCategory: ICategory = {
  imageLink: '',
  imageAlt: '',
  title: '',
  bannerCssCode: '',
};

const PlpContainer = ({ categoryId, categoryTree, data }: PlpContainerProps) => {
  const dataLayer = useTealiumDataLayer();
  const [productList, setProductList] = useState<ICategory>(defaultCategory);
  const [breadCrumbData, setBreadCrumbData] = useState<BreadcrumbProps>({ breadcrumbData: [] });
  const [searchResult, setSearchResult] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const router = useNextgenRouter();
  const selectedLocale = getLocale(router.params?.locale as string);
  const CATEGORY_DATA_NOT_FOUND = 404;

  const t = useTranslation();
  const loggerInstance = Logger.getInstance(logObject('index.tsx', 'PlpContainer'));
  const homeBreadcrumbData = getHomeBreadcrumb(t);

  const { hasNavigatedInternally } = useInternalNavigationTracker();

  const fetchBreadCrumbData = (categoryData: Category) => {
    PLPBreadcrumbTransformer(categoryData, selectedLocale)
      .then((data) => {
        if (data.breadcrumbData.length > 0) {
          setBreadCrumbData({ breadcrumbData: [homeBreadcrumbData, ...data.breadcrumbData] });
        }
      })
      .catch((e) => {
        loggerInstance.error('There is some error while generating breadcrumb data for PLP.', e);
      });
  };

  const getCategoryById = () => {
    categoryApi
      .getCategoryById({
        categoryId: categoryId,
      })
      .then((categoryData: Category | Response) => {
        if (!('id' in categoryData)) {
          setNotFound(categoryData.status === CATEGORY_DATA_NOT_FOUND);
        } else {
          setProductList(categoryListTransformer(categoryData, selectedLocale));
          fetchBreadCrumbData(categoryData);
        }
      })
      .catch(() => {
        setProductList(defaultCategory);
      });
  };

  const totalSearchResults = (res: number) => {
    setSearchResult(res);
    return res;
  };

  useEffect(() => {
    if (categoryId) {
      setProductList(defaultCategory);
      setNotFound(false);
      getCategoryById();
    }
  }, [categoryId, selectedLocale]);

  const sendPlpViewEvent = (res: Array<Product>) => {
    const transformedData = transformerRegistry[EventNameEnum.product_list_view](res);
    sendUtagView(EventNameEnum.product_list_view, dataLayer, transformedData);
  };

  const backClickHandler = () => {
    if (hasNavigatedInternally) {
      router.back();
    } else {
      router.push('/');
    }
  };

  return (
    <Layout
      hasTopMobileHeader={true}
      header={data?.layout?.header}
      footer={data?.layout?.footer}
      crumbsData={notFound ? { breadcrumbData: [] } : breadCrumbData}
      backHandler={backClickHandler}
      headerProps={{
        headerElement:
          !notFound && categoryId && typeof categoryId === 'string' ? (
            <div className={styles['product-header-card']}>
              <PLPHeaderCard
                link={productList?.imageLink ?? ''}
                alt={productList?.imageAlt ?? ''}
                categoryId={categoryId}
                categoryName={productList?.title ?? ''}
                resultsCount={searchResult}
              />
            </div>
          ) : (
            <></>
          ),
        hasTabletScrolledHeaderBody: true,
      }}
    >
      {notFound ? (
        <ErrorCard type={Errors.NotFound} onActionClick={() => router.push('/')} />
      ) : (
        <ProductListingContainer
          updatedSearchResults={totalSearchResults} // TODO: Will be updated after plp integration
          goToShop={() => {
            router.push('shop');
          }}
          searchedKeyword={categoryId}
          link={productList.imageLink}
          alt={productList.imageAlt}
          bannerTitle={productList.title}
          onProductData={sendPlpViewEvent}
          cssCode={productList.bannerCssCode}
          searchSourceKey={categoryTree}
        />
      )}
    </Layout>
  );
};

export default PlpContainer;
